<template>
  <div class="settings-wrp" :key="route.path">
    <div class="settings-title">
      {{ t("Tracking URL's") }}
    </div>
    <div class="settings-content">
      <div class="settings-block">
        <div class="settings-block__title">
          <div class="settings-block__title-text">{{ t("Click URL's For Landing Pages") }}</div>
        </div>
        <div class="mb-12">
          <span
            v-html="
              t(
                'To redirect visitors from landing page to the offer and register clicks data in the statistics it is required to place the click URL on the CTA button of landing page. <br> If your landing page contains one CTA button, use click URL. In case your LP contains more than one CTA button, add ‘/click/N’ at the end of click URL. <br> For instance, if the landing page contains three CTA buttons, the click URLs should be ended with with /click/1, /click/2 and /click/3.',
              )
            "
          />
        </div>
        <div class="mb-12">
          {{
            t(
              'For offer rotation (one CTA button - more than one offer) use flows or built-in flow in Campaigns settings.',
            )
          }}
          <a :href="campaignDocsHref" target="_blank">{{
            t('See more in our guides.')
          }}</a>
        </div>
        <ui-info class="mb-16" width="width-fit-content">
          {{ t('When using custom domain, the click URL should contain this domain.') }}
        </ui-info>
        <div class="tracking-urls__copy-wrap">
          <ui-copy-input class="pr-18" :value="clickUrl" :label="t('Click URL')" />
        </div>

        <div class="tracking-urls__copy-wrap">
          <ui-copy-input
            class="pr-18"
            :value="multiClickUrl"
            :label="t('Multi-offer click URL')"
          />
        </div>
      </div>

      <div class="settings-block">
        <div class="settings-block__title">
          <div class="settings-block__title-text">{{ t("S2S Postback URL's") }}</div>
        </div>
        <div class="mb-12">
          {{
            t(
              'To pass the conversions from affiliate network to the tracker, it is required to specify the postback on the side of affiliate network. The postback URL is available in Affiliate Networks tab.',
            )
          }}
        </div>
        <ui-info class="mb-16" width="width-fit-content">
          {{
            t(
              "Change ‘REPLACE’ and ‘OPTIONAL’ to all the tokens supported with your affiliate network, for instance, &cid={'{'}aff_sub2{'}'}&payout={'{'}payout{'}'}&txid={'{'}order_id{'}'}.",
            )
          }}
        </ui-info>
        <div class="mb-12">
          <span
            v-html="
              t(
                'Each affiliate network has dedicated tokens for postback. If any of these tokens (except click ID token) is not available with your network, you can ignore them. <br> The most important token is click ID which will pass the data on converted click ID from affiliate network to the tracker. <br>Conversions’ data is available in general reports and in Conversions tab.',
              )
            "
          />
        </div>

        <div class="tracking-urls__copy-wrap">
          <ui-copy-input class="pr-18" :value="postbackUrl" :label="t('Postback URL')" />
        </div>

        <div class="tracking-urls__copy-wrap">
          <ui-copy-input
            class="pr-18"
            :value="securePostbackClickUrl"
            :label="t('Secure postback URL')"
          />
        </div>

        <div class="tracking-urls__copy-wrap">
          <ui-copy-input
            class="pr-18"
            :value="securePostbackClickUrlTxt"
            :label-help="
              t(
                'This postback URL is required in cases when your affiliate network is requiring the response on successful conversion action in TXT format.',
              )
            "
            :label="t('Postback URL (for TXT response)')"
          />
        </div>
      </div>

      <div class="settings-block">
        <div class="settings-block__title">
          <div class="settings-block__title-text">{{ t('Conversion Pixels') }}</div>
        </div>
        <div class="mb-12">
          <span
            v-html="
              t(
                'In case postback is not supported, the alternative method of tracking conversions is conversion pixel, which should be installed on the thank you page. <br> Conversion pixels are a cookie-based solution. The click ID is stored in the initial cookie of visitor. <br> Conversion Pixel is available in three forms. The corresponding form should be selected according to the requirements of your thank you page.',
              )
            "
          />
        </div>
        <div class="tracking-urls__copy-wrap">
          <ui-copy-input
            class="pr-18"
            :value="conversionPixelUrl"
            :label="t('Conversion Pixel URL')"
          />
        </div>
        <div class="tracking-urls__copy-wrap">
          <ui-copy-input
            class="pr-18"
            :value="conversionPixel"
            :label="t('Conversion Pixel')"
          />
        </div>
        <div class="tracking-urls__copy-wrap">
          <ui-copy-input
            class="pr-18"
            :value="conversionScript"
            :label="t('Conversion Script')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import i18n from '@/i18n';
import { useI18n } from '@/composables';
import { useStore } from '@/store';
import { populateTrackingDomainsStoreCacheApiRequest } from '@/services/api/api-services/domains';

const route = useRoute();
const { t } = useI18n();
const store = useStore();
const trackingDomain = ref<TrackingDomain | null>(null);
const userTrackingDomain = ref<TrackingDomain | null>(null);

const campaignDocsHref = computed(
  () =>
    `https://docs.bemob.com/${i18n.global.locale.value}/campaign-creation-destination-settings`,
);

const clickUrl = computed(() =>
  userTrackingDomain.value ? `https://${userTrackingDomain.value.domain}/click` : '',
);

const multiClickUrl = computed(() =>
  userTrackingDomain.value ? `https://${userTrackingDomain.value.domain}/click/1` : '',
);

const postbackUrl = computed(() =>
  trackingDomain.value
    ? `http://${trackingDomain.value.domain}/postback?cid=REPLACE&payout=OPTIONAL&txid=OPTIONAL`
    : '',
);

const securePostbackClickUrl = computed(() =>
  trackingDomain.value
    ? `https://${trackingDomain.value.domain}/postback?cid=REPLACE&payout=OPTIONAL&txid=OPTIONAL`
    : '',
);

const securePostbackClickUrlTxt = computed(() =>
  trackingDomain.value
    ? `https://${trackingDomain.value.domain}/conversion.txt?cid=REPLACE&payout=OPTIONAL&txid=OPTIONAL&status=OPTIONAL `
    : '',
);

const conversionPixelUrl = computed(() =>
  trackingDomain.value
    ? `https://${trackingDomain.value.domain}/conversion.gif?cid=OPTIONAL&payout=OPTIONAL&txid=OPTIONAL`
    : '',
);

const conversionPixel = computed(() =>
  trackingDomain.value
    ? `<img src="https://${trackingDomain.value.domain}/conversion.gif?cid=OPTIONAL&payout=OPTIONAL&txid=OPTIONAL" width="1" height="1"/>`
    : '',
);

const conversionScript = computed(() =>
  trackingDomain.value
    ? '<script type="text/javascript" src="https://' +
      trackingDomain.value.domain +
      '/conversion.js?cid=OPTIONAL&payout=OPTIONAL&txid=OPTIONAL" />'
    : '',
);

onMounted(async () => {
  store.setLoading(true);
  try {
    if (!store.trackingDomains.isUploaded) {
      await populateTrackingDomainsStoreCacheApiRequest();
    }

    trackingDomain.value =
      store.trackingDomains.data.find(item => !item.custom) ?? null;
    const trackingDomainId = store.user?.trackingDomainId;
    userTrackingDomain.value =
      store.trackingDomains.data.find(item => item.id === trackingDomainId) ?? null;
  } finally {
    store.setLoading(false);
  }
});
</script>

<style lang="scss" scoped>
.tracking-urls__copy-wrap {
  width: 100%;
  max-width: 546px;
  box-sizing: border-box;
}
</style>
